import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function AboutPage() {
  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩国漫画网',
    description: '了解韩国漫画网的品牌故事、平台数据和商务合作联系方式。国内领先的韩漫免费在线阅读平台。',
    url: 'http://dakunkj.cn/about',
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Organization',
      name: '韩国漫画网',
      description: '国内领先的韩漫免费在线阅读平台',
    },
  };

  const platformSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩国漫画网',
    url: 'http://dakunkj.cn/',
    logo: 'http://dakunkj.cn/logo.png',
    description: '国内领先的韩漫免费在线阅读平台，提供恋爱、奇幻、动作、悬疑、校园、穿越、耽美、竞技等全类型韩国漫画。',
    sameAs: ['http://dakunkj.cn/'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: '客户服务',
      email: 'hello@dakunkj.cn',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutPageSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(platformSchema)}</script>
      <SEOHead />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="mt-8">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                关于韩国漫画网
              </h1>
              <p className="mt-3 text-sm text-foreground-600 max-w-2xl">
                致力于为中国读者提供最优质的韩漫在线阅读体验
              </p>
            </section>

            <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <div className="relative rounded-lg overflow-hidden aspect-[16/10]">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20cozy%20modern%20digital%20reading%20space%20with%20warm%20ambient%20lighting%2C%20tablet%20displaying%20Korean%20manhwa%20illustration%2C%20minimalist%20interior%20design%2C%20soft%20coral%20and%20warm%20beige%20tones%2C%20comfortable%20atmosphere%2C%20professional%20photography%20style%2C%20clean%20composition&width=1200&height=750&seq=about-brand-2026&orientation=landscape"
                    alt="韩国漫画网 - 品牌故事"
                    title="韩国漫画网品牌"
                    className="w-full h-full object-cover object-center"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="flex flex-col justify-center">
                <span className="text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full inline-block w-fit whitespace-nowrap">
                  品牌故事
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-4">
                  为韩漫爱好者而生
                </h2>
                <p className="mt-4 text-sm text-foreground-700 leading-relaxed">
                  韩国漫画网成立于2020年，是一群热爱韩国漫画的年轻人共同创建的平台。
                  我们深知韩漫读者对于阅读体验的追求——快速、流畅、高质量的阅读是每一个韩漫爱好者的基本需求。
                </p>
                <p className="mt-3 text-sm text-foreground-700 leading-relaxed">
                  平台以<strong>韩漫免费漫画</strong>为核心理念，精选<strong>好看的韩国漫画</strong>，采用<strong>下拉式</strong>阅读模式，
                  为用户打造极致的漫画浏览体验。从最初的几十部漫画到现在超过500部精选作品，
                  我们始终坚守初心，为读者提供优质的<strong>韩漫大全</strong>资源。
                </p>
              </div>
            </section>

            <section className="mt-20">
              <div className="text-center mb-12">
                <span className="text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full whitespace-nowrap">
                  平台数据
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-4">
                  韩国漫画网数据一览
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  用心服务每一位韩漫爱好者，用数据见证我们的成长
                </p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary-200">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500">500+</p>
                  <p className="text-sm text-foreground-600 mt-2">收录漫画作品</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary-200">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500">50万+</p>
                  <p className="text-sm text-foreground-600 mt-2">活跃读者用户</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary-200">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500">8</p>
                  <p className="text-sm text-foreground-600 mt-2">漫画分类体系</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-primary-200">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-500">2000万+</p>
                  <p className="text-sm text-foreground-600 mt-2">月度阅读量</p>
                </div>
              </div>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-accent-50 border border-accent-100 rounded-lg p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg mb-3">
                    <i className="ri-global-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">全平台覆盖</h3>
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed">
                    韩国漫画网支持手机、平板和电脑多端访问，响应式布局确保在任何设备上都能获得流畅的下拉式阅读体验。
                  </p>
                </div>
                <div className="bg-primary-50 border border-primary-100 rounded-lg p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg mb-3">
                    <i className="ri-shield-check-line text-xl text-primary-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">内容安全</h3>
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed">
                    所有收录漫画均经过严格审核，确保内容合规合法。我们重视每一位读者的阅读安全，提供绿色健康的韩漫阅读环境。
                  </p>
                </div>
                <div className="bg-secondary-50 border border-secondary-100 rounded-lg p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg mb-3">
                    <i className="ri-rocket-line text-xl text-secondary-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900">持续创新</h3>
                  <p className="text-sm text-foreground-600 mt-2 leading-relaxed">
                    我们不断优化下拉式阅读技术、图片加载速度和推荐算法，致力于为韩漫爱好者提供最极致的在线漫画浏览体验。
                  </p>
                </div>
              </div>
            </section>

            <section className="mt-20">
              <div className="text-center mb-12">
                <span className="text-xs font-medium text-primary-500 bg-primary-50 px-3 py-1 rounded-full whitespace-nowrap">
                  商务合作
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mt-4">
                  合作与联系我们
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  韩国漫画网欢迎各方合作，共同推广韩漫文化
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary-50 rounded-lg">
                      <i className="ri-briefcase-line text-2xl text-primary-500"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900">商务合作</h3>
                      <p className="text-xs text-foreground-500">漫画推广 · 品牌联动 · 内容合作</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    韩国漫画网拥有50万+活跃读者，月阅读量超2000万次。我们诚挚欢迎漫画出版社、内容平台、品牌方等合作伙伴。
                    提供漫画推广、品牌联动、内容授权等多种合作形式，助力您的作品触达海量韩漫爱好者。
                  </p>
                  <div className="mt-4 flex items-center gap-2">
                    <i className="ri-mail-line text-primary-500"></i>
                    <a href="mailto:hello@dakunkj.cn" className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap" rel="nofollow">
                      hello@dakunkj.cn
                    </a>
                  </div>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-accent-50 rounded-lg">
                      <i className="ri-customer-service-2-line text-2xl text-accent-500"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900">读者反馈</h3>
                      <p className="text-xs text-foreground-500">问题反馈 · 功能建议 · 内容投诉</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    如果您在阅读过程中遇到问题、对平台有任何建议，或发现内容侵权等情况，欢迎随时联系我们的读者服务团队。
                    我们承诺在24小时内响应每一位用户的反馈，持续优化您的韩漫阅读体验。
                  </p>
                  <div className="mt-4 flex items-center gap-2">
                    <i className="ri-mail-line text-accent-500"></i>
                    <a href="mailto:hello@dakunkj.cn" className="text-sm text-accent-500 hover:text-accent-600 whitespace-nowrap" rel="nofollow">
                      hello@dakunkj.cn
                    </a>
                  </div>
                </div>
              </div>
            </section>

            <section className="mt-20">
              <div className="text-center mb-12">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                  读者口碑
                </h2>
                <p className="mt-3 text-sm text-foreground-600">
                  来自韩漫爱好者的真实评价
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    韩漫网的更新速度和画质都是我用过最好的，下拉式阅读真的方便，上班摸鱼偷偷看太爽了。
                  </p>
                  <p className="text-xs text-foreground-500 mt-4">—— 读者 @漫画控阿杰</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    恋爱韩漫分类特别全，每周都能发现新大陆。推荐给了好多朋友，都说界面干净漂亮。
                  </p>
                  <p className="text-xs text-foreground-500 mt-4">—— 读者 @小鹿爱漫画</p>
                </div>
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-accent-400 text-xs"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    作为多年韩漫迷，韩漫网是我最常访问的网站。分类清晰，新作更新快，强烈推荐给所有韩漫爱好者。
                  </p>
                  <p className="text-xs text-foreground-500 mt-4">—— 读者 @深海漫游</p>
                </div>
              </div>
            </section>

            <section className="mt-20 pb-16">
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
                    留言联系
                  </h2>
                  <p className="mt-3 text-sm text-foreground-600">
                    有任何问题或建议？我们期待您的来信
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-lg p-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                        <i className="ri-mail-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                        <p className="text-sm text-foreground-600 mt-1">hello@dakunkj.cn</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-accent-50 rounded-lg flex-shrink-0">
                        <i className="ri-time-line text-lg text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">工作时间</p>
                        <p className="text-sm text-foreground-600 mt-1">周一至周五 9:00 - 18:00</p>
                      </div>
                    </div>
                  </div>

                  <form
                    id="about-contact-form"
                   
                    action="http://dakunkj.cn/"
                    method="POST"
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          required
                          className="w-full border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          required
                          className="w-full border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                        留言
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <div className="visually-hidden-field">
                      <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-3 rounded-md text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}